import React from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import Overlay from 'react-bootstrap/lib/Overlay';
import { injectIntl } from 'react-intl';
import moment from 'moment';

const ListContainer = ({ children, containerRef }) => (
  <div tabIndex={-1} className="dropdown-container list-container">
    <ul
      ref={containerRef}
      className="dropdown-list"
      tabIndex={-1}
      role="listbox"
      aria-labelledby="dropdown-list"
      style={{
        overflow: 'auto'
      }}
    >
      {children}
    </ul>
  </div>
);

class TimeSelectorComponent extends React.Component {
  constructor (props) {
    super(props);
    this.state = {
      show: false
    };
    this.toggleDropdown = this.toggleDropdown.bind(this);
    this.closeDropdown = this.closeDropdown.bind(this);
    this.onChangeDropdown = this.onChangeDropdown.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
  }

  componentDidUpdate () {
    const { show } = this.state;
    const { label, showStartTime, showEndTime, availability } = this.props;
    const toggleStartTime = 'Start' === label && showStartTime;
    const toggleEndTime = 'End' === label && showEndTime;

    // To force toggleDropdown once date or start time is selected.
    if ((toggleStartTime || toggleEndTime) && !show && availability.length) {
      this.toggleDropdown();
    }
  }

  onChangeDropdown (event) {
    const { onChangeHandler } = this.props;
    onChangeHandler(event, () => {
      this.setState({ show: false });
    });
  }

  onKeyPress (event) {
    event.preventDefault();
    event.stopPropagation();

    if (13 === event.keyCode) {
      if (-1 !== event.target.tabIndex) {
        this.onChangeDropdown(event);
      }
    }
    if (40 === event.keyCode && event.target.nextElementSibling) {
      event.target.nextElementSibling.focus();
    }
    if (38 === event.keyCode && event.target.previousElementSibling) {
      event.target.previousElementSibling.focus();
    }
  }

  toggleDropdown () {
    const { show } = this.state;
    const { selectValue, availabilityFn, dropDownToRender, capturedDate, availability } = this.props;
    // availabilityFn: function from date-time-input getDayAvailability
    const toggle = () => {
      this.setState({ show: !show }, () => {
        // get the first available time and focus
        const firstAvailability = availability[0].time_index;
        const listItem = this[`list-${selectValue || firstAvailability || '0'}`];
        if (listItem) {
          listItem.focus();
        }
      });
    };
    if (capturedDate) {
      if ('capturedStartTime' === dropDownToRender) {
        return availabilityFn('start', true, toggle);
      }
      return availabilityFn('end', null, toggle);
    }
    return toggle();
  }

  // Call function from parent component to alter showStartTime or showEndTime prop values
  // in order to force close dropdown when clicking outside of it.
  closeDropdown () {
    const { show } = this.state;
    const { label, changeShowState } = this.props;
    changeShowState(label, !show);
    this.setState({ show: false });
  }

  render () {
    const {
      label,
      placeholder,
      dropDownToRender,
      selectValue,
      availability,
      selectDisabled,
      htmlId,
      input,
      intl
    } = this.props;

    const { show } = this.state;
    const disabledColor = selectDisabled ? { color: '#CED2DA' } : {};
    const selectValueInt = parseInt(selectValue, 10);
    const filteredAvailability = _.filter(availability, { time_index: selectValueInt });
    const selectedFormattedTime = filteredAvailability.length ? intl.formatTime(moment(filteredAvailability[0].time, 'LT').format()) : placeholder;
    return (
      <div>
        { label ? <label className="time-label" htmlFor={dropDownToRender}>{label} </label> : '' }
        <div className="dropdown-container">
          <button
            id={`${htmlId}-${dropDownToRender}`}
            data-testing-id='time-selector-btn'
            type="button"
            className={`btn dropdown-btn btn-block text-gray-dark ${filteredAvailability.length ? 'value-selected' : ''}`}
            style={disabledColor}
            onClick={this.toggleDropdown}
            disabled={selectDisabled}
            ref={button => { this.target = button; }}
            aria-haspopup="listbox"
            aria-expanded={show}
          >
            <span className="dropdown-value ellipsis" {...input}>
              {selectedFormattedTime}
            </span>
            <span className="down-arrow" />
          </button>
          <Overlay
            show={show}
            rootClose
            onHide={this.closeDropdown}
            placement="bottom"
            animation={false}
            container={this}
            target={this.target}
          >
            <ListContainer
              containerRef={ref => { this.dropdownList = ref; }}
            >
              {
                _.map(availability, (value) => {
                  const { time_index: timeIndex, time, available = true } = value;
                  const formattedTime = intl.formatTime(moment(time, 'LT').format());
                  const rxKey = `${dropDownToRender}-${timeIndex}`;
                  const selected = timeIndex === selectValueInt;
                  return (
                    <li
                      ref={(ref) => { this[`list-${timeIndex}`] = ref; }}
                      role="option"
                      aria-label={time}
                      onClick={this.onChangeDropdown}
                      className={`${selected ? 'selected' : ''} ${!available ? 'disabled' : ''}`}
                      aria-selected={selected}
                      key={rxKey}
                      data-key={dropDownToRender}
                      data-value={timeIndex}
                      tabIndex={available ? '0' : '-1'}
                      onKeyDown={this.onKeyPress}
                    >
                      {formattedTime}
                    </li>
                  );
                })
              }
            </ListContainer>
          </Overlay>
        </div>
      </div>
    );
  }
}

ListContainer.propTypes = {
  children: PropTypes.node,
  containerRef: PropTypes.func
};

TimeSelectorComponent.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  dropDownToRender: PropTypes.string,
  selectValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  availability: PropTypes.array,
  onChangeHandler: PropTypes.func,
  selectDisabled: PropTypes.bool,
  availabilityFn: PropTypes.func,
  capturedDate: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object
  ]),
  htmlId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ]),
  changeShowState: PropTypes.func,
  showStartTime: PropTypes.bool,
  showEndTime: PropTypes.bool
};


export const TimeSelector = injectIntl(TimeSelectorComponent);
